import { ChangeDetectionStrategy, Component, Input, OnChanges } from '@angular/core';
import * as moment from 'moment';
import { UpdateVariableBenefitConfigurationSubmission } from '../../../../../../models/submissions/update-variable-benefit-configuration-submission';
import { VariableBenefitPaymentChangeSubmission } from '../../../../../../models/submissions/variable-benefit-payment-change-submission';
import { VariableBenefitPaymentFrequencyEnum } from '../../../../../../models/enum/shared/variable-benefit-payment-frequency.enum';
import { UpdateVbConfigRecurringPaymentReviewCardComponent } from './update-vb-config-recurring-payment-review-card/update-vb-config-recurring-payment-review-card.component';
import {
  VbPaymentJurisdictionDetailsUtils,
  VbPaymentSubmissionData
} from '../../utils/vb-payment-jurisdiction-details.utils';

type RecurringPaymentsSubmission =
  | UpdateVariableBenefitConfigurationSubmission
  | VariableBenefitPaymentChangeSubmission;
type RecurringPaymentsSubmissionData = VbPaymentSubmissionData;

interface RecurringPaymentComparison {
  useMinimumAmount: boolean;
  useMaximumAmount: boolean;
  amount: number | null;
}

@Component({
  selector: 'app-update-vb-config-recurring-payments-review-card',
  templateUrl: './update-vb-config-recurring-payments-review-card.component.html',
  styleUrls: ['./update-vb-config-recurring-payments-review-card.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class UpdateVbConfigRecurringPaymentsReviewCardComponent implements OnChanges {
  @Input({ required: true }) submission!: RecurringPaymentsSubmission;
  @Input() newEstimatedAnnualTotal: number | null = null;

  public isUnchanged = false;
  public showCurrentCard = false;
  public currentSubtitle: string | null = null;
  public newSubtitle: string | null = null;
  public newJurisdictionKeys: string[] = [];

  public ngOnChanges(): void {
    const previous = this.submission?.previous ?? null;
    const newData = this.submission?.new ?? null;

    this.isUnchanged = this.areRecurringPaymentsEquivalent(previous, newData);
    this.showCurrentCard = this.shouldShowCurrentCard(previous);
    this.currentSubtitle = this.buildCurrentSubtitle(previous, newData);
    this.newSubtitle = this.buildNewSubtitle(newData);
    this.newJurisdictionKeys = this.findNewJurisdictionKeys(previous, newData);
  }

  private areRecurringPaymentsEquivalent(
    previous: RecurringPaymentsSubmissionData | null,
    newData: RecurringPaymentsSubmissionData | null
  ): boolean {
    if (!previous || !newData) {
      return false;
    }
    if (previous.recurringPaymentFrequencyId !== newData.recurringPaymentFrequencyId) {
      return false;
    }
    const previousByKey = this.recurringPaymentsByKey(previous);
    const newByKey = this.recurringPaymentsByKey(newData);
    if (previousByKey.size !== newByKey.size) {
      return false;
    }
    return Array.from(previousByKey.entries()).every(([key, previousPayment]) => {
      const newPayment = newByKey.get(key);
      return (
        newPayment !== undefined &&
        previousPayment.useMinimumAmount === newPayment.useMinimumAmount &&
        previousPayment.useMaximumAmount === newPayment.useMaximumAmount &&
        previousPayment.amount === newPayment.amount
      );
    });
  }

  private recurringPaymentsByKey(data: RecurringPaymentsSubmissionData): Map<string, RecurringPaymentComparison> {
    const entries = VbPaymentJurisdictionDetailsUtils.paymentDetails(data).map(
      (detail): [string, RecurringPaymentComparison] => [
        UpdateVbConfigRecurringPaymentReviewCardComponent.jurisdictionKey(detail),
        {
          useMinimumAmount: detail.recurringPaymentUseMinimumAmount,
          useMaximumAmount: detail.recurringPaymentUseMaximumAmount,
          amount: detail.recurringPaymentAmount
        }
      ]
    );
    return new Map(entries);
  }

  private shouldShowCurrentCard(previous: RecurringPaymentsSubmissionData | null): boolean {
    if (!previous || !previous.recurringPaymentStartDate) {
      return false;
    }
    // Hide if the previous config's payments hadn't started yet (start date is still in the future).
    return moment(previous.recurringPaymentStartDate).isSameOrBefore(moment(), 'day');
  }

  private buildCurrentSubtitle(
    previous: RecurringPaymentsSubmissionData | null,
    newData: RecurringPaymentsSubmissionData | null
  ): string | null {
    if (!previous) {
      return null;
    }
    const previousIsMonthly = previous.recurringPaymentFrequencyId === VariableBenefitPaymentFrequencyEnum.Monthly;
    if (previousIsMonthly) {
      if (!newData?.recurringPaymentStartDate) {
        return null;
      }
      const lastMonth = moment.utc(newData.recurringPaymentStartDate).subtract(1, 'month').format('MMMM YYYY');
      return $localize`Last payment in ${lastMonth}`;
    }
    if (!previous?.recurringPaymentStartDate) {
      return null;
    }
    const annualMonth = moment.utc(previous.recurringPaymentStartDate).format('MMMM YYYY');
    return $localize`Last payment in ${annualMonth}`;
  }

  private buildNewSubtitle(newData: RecurringPaymentsSubmissionData | null): string | null {
    if (!newData?.recurringPaymentStartDate) {
      return null;
    }
    const month = moment.utc(newData.recurringPaymentStartDate).format('MMMM YYYY');
    return $localize`Starting ${month}`;
  }

  private findNewJurisdictionKeys(
    previous: RecurringPaymentsSubmissionData | null,
    newData: RecurringPaymentsSubmissionData | null
  ): string[] {
    if (!newData) {
      return [];
    }
    const previousKeys = new Set(
      VbPaymentJurisdictionDetailsUtils.paymentDetails(previous).map(d =>
        UpdateVbConfigRecurringPaymentReviewCardComponent.jurisdictionKey(d)
      )
    );
    return VbPaymentJurisdictionDetailsUtils.paymentDetails(newData)
      .map(d => UpdateVbConfigRecurringPaymentReviewCardComponent.jurisdictionKey(d))
      .filter(key => !previousKeys.has(key));
  }
}
